import React, { useState, useEffect } from 'react';
import { X, Plus, CheckCircle } from 'lucide-react';
import { NFR } from '../../services/workshop';

interface NFREditModalProps {
  nfr: NFR;
  createMode?: boolean;
  onSave: (nfrId: string, patch: Partial<NFR>) => Promise<void>;
  onClose: () => void;
}

const CATEGORIES: NFR['category'][] = [
  'performance', 'security', 'scalability', 'availability',
  'usability', 'maintainability', 'compliance',
];

export function NFREditModal({ nfr, createMode, onSave, onClose }: NFREditModalProps) {
  const [title, setTitle] = useState(nfr.title);
  const [description, setDescription] = useState(nfr.description);
  const [priority, setPriority] = useState<NFR['priority']>(nfr.priority);
  const [category, setCategory] = useState<NFR['category']>(nfr.category);
  const [acceptanceCriteria, setAcceptanceCriteria] = useState<{ id: string; value: string }[]>(
    (nfr.acceptanceCriteria || []).map((v, i) => ({ id: `ac-${i}-${v.slice(0, 8)}`, value: v }))
  );
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const handleSave = async () => {
    if (!title.trim()) return;
    setSaving(true);
    try {
      await onSave(nfr.id, { title: title.trim(), description: description.trim(), priority, category, acceptanceCriteria: acceptanceCriteria.map(a => a.value) });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const updateAC = (id: string, val: string) =>
    setAcceptanceCriteria(prev => prev.map(a => a.id === id ? { ...a, value: val } : a));
  const removeAC = (id: string) =>
    setAcceptanceCriteria(prev => prev.filter(a => a.id !== id));
  const addAC = () => setAcceptanceCriteria(prev => [...prev, { id: `ac-new-${Date.now()}`, value: '' }]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#e2e8f0]">
          <h2 className="text-base font-bold text-navy">{createMode ? 'Add NFR' : 'Edit NFR'}</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 transition-colors">
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 p-6 flex flex-col gap-5">
          {/* Title */}
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Title</label>
            <input
              value={title}
              onChange={e => setTitle(e.target.value)}
              className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Description</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              rows={6}
              className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none resize-none"
            />
          </div>

          {/* Priority + Category row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Priority</label>
              <div className="flex rounded-xl border border-[#e2e8f0] overflow-hidden">
                {(['high', 'medium', 'low'] as const).map(p => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setPriority(p)}
                    className={`flex-1 py-2 text-xs font-semibold transition-all capitalize ${
                      priority === p ? 'bg-primary text-white' : 'bg-white text-slate-500 hover:bg-slate-50'
                    }`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Category</label>
              <select
                value={category}
                onChange={e => setCategory(e.target.value as NFR['category'])}
                className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2 text-sm text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-white capitalize"
              >
                {CATEGORIES.map(c => (
                  <option key={c} value={c} className="capitalize">{c}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Acceptance criteria */}
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 flex items-center gap-1">
              <CheckCircle size={11} />Acceptance Criteria
            </label>
            <div className="flex flex-col gap-1.5">
              {acceptanceCriteria.map(ac => (
                <div key={ac.id} className="flex items-center gap-2">
                  <input
                    value={ac.value}
                    onChange={e => updateAC(ac.id, e.target.value)}
                    className="flex-1 border border-[#e2e8f0] rounded-xl px-3 py-2 text-xs text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                  />
                  <button
                    onClick={() => removeAC(ac.id)}
                    className="w-6 h-6 flex items-center justify-center rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors flex-shrink-0"
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
              <button
                onClick={addAC}
                className="flex items-center gap-1 text-xs text-primary hover:text-primary-dark font-semibold transition-colors w-fit"
              >
                <Plus size={11} /> Add criterion
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-[#e2e8f0] bg-slate-50/50">
          <button
            onClick={onClose}
            className="flex items-center gap-1.5 rounded-xl h-9 px-4 border border-slate-200 bg-white text-slate-600 text-sm font-semibold hover:bg-slate-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !title.trim()}
            className="flex items-center gap-1.5 rounded-xl h-9 px-5 bg-primary hover:bg-primary-dark transition-colors text-white text-sm font-bold shadow-sm disabled:opacity-60"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
